/**
 * Import d'un export **Proton Pass** JSON (R7) : le fichier `data.json` de
 * l'archive `Proton Pass.zip`, ou le JSON seul.
 *
 * Purement local, aucune E/S reseau : ce module transforme un export en
 * `ImportedEntry`. Les coffres Proton deviennent des dossiers, la corbeille
 * est ignoree, les champs supplementaires deviennent des champs personnalises
 * et aucun contenu non reconnu n'est perdu (il part en notes). Le mot de passe
 * est date de la derniere modification de l'element (`modifyTime`) pour la
 * Sante du coffre.
 *
 * Un export Proton **chiffre** est protege par OpenPGP : il n'est pas lisible
 * ici et est refuse avec la marche a suivre, jamais silencieusement.
 */
import type { EntryCustomField } from '@klavox/crypto-core';
import type { VaultItemType } from '@klavox/vault-model';
import { joinNotes, normalizeTotpSecret, type ImportedEntry } from './csv-import.js';
import type { SshKeyMaterial } from './ssh-key-item.js';
import { normalizeTypedFields, typedFieldGroup, wifiSecurity, type WifiSecurity } from './typed-fields.js';

/** `data.type` des elements Proton Pass -> type d'entree Klavox. */
const PROTON_TYPES: Record<string, VaultItemType> = {
  login: 'login',
  alias: 'login',
  note: 'note',
  creditCard: 'card',
  identity: 'identity',
  sshKey: 'ssh-key',
  wifi: 'wifi',
  custom: 'note',
};

/** Champs `identity` de Proton Pass -> champs types Klavox (`typed-fields.ts`). */
const PROTON_IDENTITY_FIELDS: Record<string, string> = {
  firstName: 'firstName',
  lastName: 'lastName',
  birthdate: 'birthDate',
  email: 'email',
  phoneNumber: 'phone',
  organization: 'company',
  streetAddress: 'address',
  zipOrPostalCode: 'postalCode',
  city: 'city',
  stateOrProvince: 'region',
  countryOrRegion: 'country',
  socialSecurityNumber: 'socialSecurityNumber',
  passportNumber: 'passportNumber',
  licenseNumber: 'licenseNumber',
};

/** `WifiSecurity` de Proton Pass (enum protobuf, exporte en nombre ou en nom) -> valeur CXF ; 0 = non renseignee. */
const PROTON_WIFI_SECURITY: Record<string, WifiSecurity> = { '1': 'wpa-personal', '2': 'wpa2-personal', '3': 'wpa3-personal', '4': 'wep' };

/** Champs de contenu deja portes par un champ natif : pas de doublon en notes. */
const CONSUMED_LOGIN_KEYS = new Set(['itemEmail', 'itemUsername', 'username', 'password', 'urls', 'totpUri', 'passkeys']);

export class ProtonImportError extends Error {
  override readonly name = 'ProtonImportError';
  constructor(message: string) {
    super(`export Proton Pass illisible : ${message}`);
  }
}

type Json = null | boolean | number | string | Json[] | { [key: string]: Json };
type JsonObject = { [key: string]: Json };

function asObject(value: Json | undefined): JsonObject | null {
  return value !== null && value !== undefined && typeof value === 'object' && !Array.isArray(value) ? value : null;
}

function asArray(value: Json | undefined): Json[] {
  return Array.isArray(value) ? value : [];
}

/** Instant Proton (secondes) -> ms ; absent, nul ou illisible = `null`. */
function protonInstant(value: Json | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? Math.round(value * 1000) : null;
}

function asString(value: Json | undefined): string {
  return typeof value === 'string' ? value : typeof value === 'number' ? String(value) : '';
}

/** `MMYYYY` (Proton) ou `YYYY-MM` -> `MM/YYYY` attendu par le champ « Expiration ». */
function normalizeExpiry(raw: string): string {
  const value = raw.trim();
  if (/^\d{6}$/u.test(value)) return `${value.slice(0, 2)}/${value.slice(2)}`;
  if (/^\d{4}-\d{2}$/u.test(value)) return `${value.slice(5)}/${value.slice(0, 4)}`;
  return value;
}

/** Type declare (`data.type`) ou, sur les exports anciens, deduit du contenu. */
function itemType(data: JsonObject, content: JsonObject): VaultItemType {
  const declared = PROTON_TYPES[asString(data['type'])];
  if (declared !== undefined) return declared;
  if (content['number'] !== undefined || content['verificationNumber'] !== undefined) return 'card';
  if (content['fullName'] !== undefined || content['firstName'] !== undefined) return 'identity';
  if (content['password'] !== undefined || content['urls'] !== undefined) return 'login';
  return 'note';
}

function parseProtonItem(raw: JsonObject, vaultName: string): ImportedEntry | null {
  // `state` 2 = corbeille.
  if (Number(raw['state'] ?? 1) === 2) return null;
  const data = asObject(raw['data']);
  if (data === null) return null;
  const metadata = asObject(data['metadata']) ?? {};
  const content = asObject(data['content']) ?? {};
  const type = itemType(data, content);
  const extras: string[] = [];
  const customFields: EntryCustomField[] = [];
  const typed: Record<string, string> = {};
  let sshKey: SshKeyMaterial | null = null;

  if (type === 'card') {
    typed['cardholder'] = asString(content['cardholderName']);
    typed['number'] = asString(content['number']);
    typed['cvv'] = asString(content['verificationNumber']);
    typed['pin'] = asString(content['pin']);
    typed['expiry'] = normalizeExpiry(asString(content['expirationDate']));
  } else if (type === 'identity') {
    for (const [source, key] of Object.entries(PROTON_IDENTITY_FIELDS)) {
      const value = asString(content[source]);
      if (value.length > 0) typed[key] = value;
    }
    const fullName = asString(content['fullName']);
    if (fullName.length > 0 && typed['firstName'] === undefined && typed['lastName'] === undefined) {
      const [first, ...rest] = fullName.split(/\s+/u);
      typed['firstName'] = first ?? '';
      typed['lastName'] = rest.join(' ');
    }
  } else if (type === 'ssh-key') {
    // La cle privee ne passe JAMAIS en notes (affichees en clair) : elle devient la paire de l'entree.
    sshKey = { privateKey: asString(content['privateKey']), publicKey: asString(content['publicKey']) };
  } else if (type === 'wifi') {
    // Le mot de passe du reseau reste `content.password`, lu plus bas comme celui d'un identifiant.
    typed['ssid'] = asString(content['ssid']);
    const rawSecurity = asString(content['security']);
    const security = PROTON_WIFI_SECURITY[rawSecurity] ?? wifiSecurity(rawSecurity);
    if (security !== null) typed['security'] = security;
    else if (rawSecurity.length > 0 && !['0', 'UnspecifiedWifiSecurity'].includes(rawSecurity)) extras.push(`Securite Wi-Fi : ${rawSecurity}`);
  } else {
    // Note, element personnalise : tout contenu non natif part en notes.
    for (const [key, value] of Object.entries(content)) {
      if (CONSUMED_LOGIN_KEYS.has(key)) continue;
      const text = asString(value);
      if (text.length > 0) extras.push(`${key} : ${text}`);
    }
  }

  for (const rawField of asArray(data['extraFields'])) {
    const field = asObject(rawField);
    if (field === null) continue;
    const value = asObject(field['data']);
    const text = value === null ? '' : Object.values(value).map((part) => asString(part)).filter(Boolean).join(', ');
    // `hidden` (champ masque) et `totp` (graine) restent masques a l'ecran.
    const hidden = ['hidden', 'totp'].includes(asString(field['type']));
    if (text.length > 0) customFields.push({ name: asString(field['fieldName']), value: text, hidden });
  }

  const aliasEmail = asString(raw['aliasEmail']);
  if (aliasEmail.length > 0) extras.push(`Alias e-mail : ${aliasEmail}`);
  const passkeys = asArray(content['passkeys']).length;
  // Les passkeys Proton ne sont pas importables tant que Klavox n'en stocke pas (R13) : on le DIT.
  if (passkeys > 0) extras.push(`${passkeys} passkey(s) non importee(s) : elles restent dans Proton Pass`);

  const totp = normalizeTotpSecret(asString(content['totpUri']));
  if (totp.extraNote.length > 0) extras.push(totp.extraNote);
  const uris = asArray(content['urls']).map(asString).filter((url) => url.length > 0);
  const username = asString(content['itemUsername']) || asString(content['username']) || asString(content['itemEmail']) || aliasEmail;
  const group = typedFieldGroup(type);
  const folderName = vaultName.trim();
  const password = asString(content['password']);
  // Proton ne date pas le mot de passe lui-meme : derniere modification de l'element, une borne haute, jamais
  // `createTime` (un mot de passe change depuis passerait pour plus ancien qu'il n'est : faux « Site compromis »).
  const passwordChangedAt = password.length > 0 ? protonInstant(raw['modifyTime']) : null;
  return {
    type,
    name: asString(metadata['name']) || uris[0] || username || typed['ssid'] || 'Sans nom',
    username,
    password,
    uris,
    notes: joinNotes([asString(metadata['note']), ...extras]),
    totpSecret: totp.secret,
    favorite: raw['pinned'] === true,
    ...(passwordChangedAt !== null ? { passwordChangedAt } : {}),
    ...(customFields.length > 0 ? { customFields } : {}),
    ...(folderName.length > 0 ? { folderPath: [folderName] } : {}),
    ...(sshKey !== null ? { sshKey } : {}),
    ...(group !== null ? { fields: normalizeTypedFields(group, typed) } : {}),
  };
}

/** Un export Proton Pass se reconnait a son dictionnaire `vaults`. */
export function isProtonExport(data: Json): boolean {
  return asObject(asObject(data)?.['vaults']) !== null;
}

/**
 * Parse un export Proton Pass (`data.json`). Chaque coffre Proton devient un
 * dossier du coffre Klavox.
 *
 * @throws {ProtonImportError} export chiffre (OpenPGP) ou structure inattendue
 */
export function parseProtonExport(data: Json): ImportedEntry[] {
  return readProtonExport(data).entries;
}

/** Entrees ET coffres Proton, VIDES compris : un coffre prepare sans element reste un dossier au demenagement. */
export function readProtonExport(data: Json): { entries: ImportedEntry[]; folders: string[][] } {
  const file = asObject(data);
  const vaults = asObject(file?.['vaults']);
  if (file === null || vaults === null) throw new ProtonImportError('structure inattendue (aucun coffre)');
  if (file['encrypted'] === true) {
    throw new ProtonImportError('export chiffre (OpenPGP) : reexportez depuis Proton Pass sans chiffrement PGP');
  }
  const entries: ImportedEntry[] = [];
  const folders: string[][] = [];
  for (const rawVault of Object.values(vaults)) {
    const vault = asObject(rawVault);
    if (vault === null) continue;
    const name = asString(vault['name']);
    if (name.trim().length > 0 && !folders.some(([known]) => known === name.trim())) folders.push([name.trim()]);
    for (const rawItem of asArray(vault['items'])) {
      const item = asObject(rawItem);
      const entry = item === null ? null : parseProtonItem(item, name);
      if (entry !== null) entries.push(entry);
    }
  }
  return { entries, folders };
}
